<template>
  <div class="relative" ref="dropdownContainer">
    <button
      @click.stop="toggleDropdown"
      type="button"
      class="flex items-center justify-center bg-gray-100 rounded-lg px-1 py-1"
    >
      <DotsVerticalIcon color="#181D27" />
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
    >
      <button
        @click="handleReopen"
        :disabled="isProcessing"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="mr-3 text-gray-600">
          <EditIcon />
        </span>
        <Translation name="report.reopen_to_edit" default="Reopen to Edit" />
      </button>
      <button
        @click="handleArchive"
        :disabled="isProcessing"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="mr-3 text-gray-600">
          <ArchiveIcon />
        </span>
        <Translation name="report.archive" default="Archive" />
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import EditIcon from "./icons/EditIcon.vue";
import ArchiveIcon from "./icons/ArchiveIcon.vue";
import DotsVerticalIcon from "./icons/DotsVerticalIcon.vue";

export default {
  name: "ReportActionsDropdown",
  components: { EditIcon, ArchiveIcon, DotsVerticalIcon },
  props: {
    calculationId: {
      type: [Number, String],
      required: true,
    },
    reopenUrl: {
      type: String,
      required: true,
    },
    archiveUrl: {
      type: String,
      required: true,
    },
    redirectAfterArchiveUrl: {
      type: String,
      required: true,
    },
    confirmArchiveMessage: {
      type: String,
      default: "Are you sure you want to archive this calculation?",
    },
  },
  setup(props) {
    const isOpen = ref(false);
    const isProcessing = ref(false);
    const dropdownContainer = ref(null);

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const handleReopen = () => {
      if (isProcessing.value) return;
      isProcessing.value = true;

      // Create a form and submit it as PATCH request
      const form = document.createElement("form");
      form.method = "POST";
      form.action = props.reopenUrl;

      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = "PATCH";
      form.appendChild(methodInput);

      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      ).content;
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = csrfToken;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
    };

    const handleArchive = async () => {
      if (isProcessing.value) return;

      if (!confirm(props.confirmArchiveMessage)) {
        closeDropdown();
        return;
      }

      isProcessing.value = true;

      try {
        const response = await fetch(props.archiveUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        });

        const data = await response.json();

        if (data.success) {
          window.location.href = props.redirectAfterArchiveUrl;
        } else {
          alert(data.message || "Failed to archive calculation");
        }
      } catch (error) {
        console.error("Archive error:", error);
        alert("Failed to archive calculation");
      } finally {
        isProcessing.value = false;
        closeDropdown();
      }
    };

    const handleClickOutside = (event) => {
      if (
        dropdownContainer.value &&
        !dropdownContainer.value.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      isProcessing,
      dropdownContainer,
      toggleDropdown,
      handleReopen,
      handleArchive,
    };
  },
};
</script>
